import type { GroupBase } from 'react-select';

import type {
  Filter,
  Operation,
  ColumnOrderInline,
  ColumnNameDateRangePresetValueInline,
  FilterOperatorInline,
  OperationOrderInline,
  TemplateDtoReportingFormatInline,
} from '@/services/ws-bi/src/main/resources/rest-api';
import type { LanguageCode } from '@/services/ws-crm/src/main/resources/rest-api';
import type { DateTimeUnixType } from '@/js/libs/api-helpers';
import type { SelectKeys } from '@/js/libs/utils/types';

import type { Option, ValidationErrors } from 'src/types';
import type { DateExtractInterval, DateHistogramInterval } from '../../types';

export type AllowedDirectives<C> = keyof Omit<C, 'order' | 'operator' | 'andFilters' | 'orFilters'>;
export type AllowedOperations<C> = C[AllowedDirectives<C>];

export interface SelectedFieldOption extends Option<string> {
  label: string;
  value: string;
  group: string;
}

export interface SelectedDateRangeOption {
  label: string;
  value: ColumnNameDateRangePresetValueInline;
}

export interface SelectedOperatorOption {
  label: string;
  value: FilterOperatorInline;
}

export interface SelectedDateHistogramIntervalOption {
  label: string;
  value: DateHistogramInterval;
}

export interface SelectedDateExtractIntervalOption {
  label: string;
  value: DateExtractInterval;
}

interface SelectedOperationGeneric {
  id: string;
  name: SelectedFieldOption | null;
  operation: Option<AllowedDirectives<Operation>> | null;
  order: SelectedOrder | undefined;
}

export type SelectedOperationColumnName = SelectedOperationGeneric;

export interface SelectedOperationColumnNameValue extends SelectedOperationGeneric {
  value: { label: string; value: string } | string | null;
}

export type SelectedOperation = SelectedOperationColumnName | SelectedOperationColumnNameValue;

interface SelectedFilterGeneric {
  id: string;
  name: SelectedFieldOption | null;
  operation: Option<AllowedDirectives<Filter>> | null;
  operator: SelectedOperatorOption | null;
}

export type SelectedFilterColumnName = SelectedFilterGeneric;

export interface SelectedFilterColumnNameValueString extends SelectedFilterGeneric {
  value: DateTimeUnixType | number | string | null;
}

export interface SelectedFilterColumnNameValueBoolean extends SelectedFilterGeneric {
  value: boolean | null;
}

export interface SelectedFilterColumnNameRange extends SelectedFilterGeneric {
  from: DateTimeUnixType | null;
  to: DateTimeUnixType | null;
}

export interface SelectedFilterColumnNameDateRangePreset extends SelectedFilterGeneric {
  value: SelectedDateRangeOption | null;
}

export type SelectedFilter =
  | SelectedFilterColumnName
  | SelectedFilterColumnNameValueString
  | SelectedFilterColumnNameRange
  | SelectedFilterColumnNameDateRangePreset
  | SelectedFilterColumnNameValueBoolean;

export type NestedFilters = {
  values: Array<SelectedFilter | NestedFilters>;
  group: FilterGroupRelation.AND | FilterGroupRelation.OR;
};

export type SelectedOrder = {
  label: string;
  value: OperationOrderInline | undefined;
};

export interface SelectedFieldOptionGroup extends GroupBase<SelectedFieldOption> {
  label: string;
  options: Array<SelectedFieldOption>;
}

export interface SelectedRowsLimitOption {
  label: string;
  value: number;
}

export interface SelectedColumn {
  name: string;
  visible: boolean;
  order?: ColumnOrderInline;
}

export interface SelectedFormat {
  name: TemplateDtoReportingFormatInline;
  value: TemplateDtoReportingFormatInline;
}

export interface SelectedLanguage {
  name: LanguageCode;
  value: LanguageCode;
}

export type FormState = {
  name: string;
  type: string;
  columns: SelectedColumn[];
  nestedFilters: NestedFilters;
  groups: SelectedOperation[];
  totals: SelectedOperation[];
  rowsLimit: SelectedRowsLimitOption | null;
  subRowsLimit: SelectedRowsLimitOption;
  isAutoMailed: boolean;
  accessibility: boolean;
  reportingFormat: Option<SelectedFormat['value']>;
  language: Option<SelectedLanguage['value']>;
  reportMailCron: string | null;
  mailAddresses: string[];
  refresh?: number;
};

export type ExactOperation<Directive extends keyof Operation> = Required<Pick<Operation, Directive>> & Pick<Operation, 'order'>;

export type ExactFilter<Directive extends keyof Filter> = Required<Pick<Filter, Directive>> & Pick<Filter, 'operator'>;

// nested exact filter is the same but since its nested there is no need for operator
export type NestedExactFilter<Directive extends keyof Filter> = Omit<ExactFilter<Directive>, 'operator'>;

export type AllowedTypesObject<C extends Record<string, unknown>, O> = Required<{
  [key in AllowedDirectives<C>]: key extends SelectKeys<C, O | undefined, 'equals'> ? true : false;
}>;

export type SaveTemplateFormState = {
  id?: string;
  name: string;
  clone: boolean;
};

export type SaveTemplateFormErrorState = ValidationErrors<SaveTemplateFormState>;

export enum SortDirections {
  ASC = 'ASC',
  DESC = 'DESC',
}

export enum SortKeys {
  name = 'name',
  accessibility = 'accessibility',
  createdBy = 'createdBy',
  createdDate = 'createdDate',
  lastModifiedBy = 'lastModifiedBy',
  lastModifiedDate = 'lastModifiedDate',
}

export enum FilterGroupRelation {
  AND = 'AND',
  OR = 'OR',
}

export type FilterError = ValidationErrors<SelectedFilter>;

export type NestedFiltersErrors = {
  values?: Array<FilterError | NestedFiltersErrors | undefined>;
};
